<script setup lang="ts">
import {
  Clock, PanelLeft, PanelLeftClose, FolderOpen,
  Landmark, FilePen, TableProperties,
  MessageSquare, MessageSquarePlus, Upload, Shield, Star, Building2, Share2,
} from 'lucide-vue-next'
import { ALLOWED_ACCEPT } from '~~/shared/allowedFileTypes'
import { useJurisdictionName } from '~/composables/useJurisdictionName'
import { useUploadTargetStore } from '~/stores/uploadTarget'
import { useSharedChatsStore } from '~/stores/sharedChats'
import ReminderBubble from '~/components/onboarding/ReminderBubble.vue'
import type { OptionalStep } from '#shared/onboarding/flow'
import {
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  isContentRoute,
} from '~/composables/usePanelLayout'

const { t } = useI18n()
const jurisdictionName = useJurisdictionName()
const route = useRoute()
const { initials, displayName, organizationName, organizationMainJurisdiction, profile, status: profileStatus } = useUserProfile()
const appEnv = useAppEnv()
const {
  sidebarWidth,
  isSidebarCollapsed,
  isSidebarPeeking,
  isSidebarResizing,
  docsDrawerOpen,
  chatPanelOpen,
  toggleRail,
  setSidebarResizing,
  handleSidebarDrag,
  scheduleDismissSidebarPeek,
  cancelDismissSidebarPeek,
  toggleDocsDrawer,
  toggleChatPanel,
} = usePanelLayout()

const sidebarState = computed<'docked' | 'collapsed' | 'peeking'>(() => {
  if (!isSidebarCollapsed.value) return 'docked'
  return isSidebarPeeking.value ? 'peeking' : 'collapsed'
})

// Loaded once per session and shared (useState), so the rail costs one request regardless of how many
// components ask. Failure is silent — see useOrgUsageMeter.
const { primary: usageMeter, load: loadUsageMeter } = useOrgUsageMeter()
onMounted(loadUsageMeter)
/**
 * The rail bar shows what is LEFT, not what is spent — it starts full and drains. A filling bar reads
 * as progress toward something good; this is a fuel gauge, and the question it answers is "how much
 * room do I have". Empty therefore means blocked, which is the state worth noticing at a glance.
 */
const usageMeterRemaining = computed(() => {
  const m = usageMeter.value
  if (!m) return 0
  if (m.blocked) return 0
  // Keep a sliver visible while any allowance remains, so "nearly out" never renders as "out".
  return Math.max(2, 100 - Math.min(100, m.percentUsed))
})

/**
 * Pages are quotable ("312 pages remaining"); a token allowance is not, so those categories get the
 * percentage instead — see `shared/usage/orgView.ts` (MAR-112).
 */
const usageMeterTitle = computed(() => {
  const m = usageMeter.value
  if (!m) return ''
  const category = t(`usage.category.${m.category}`)
  if (!m.limited) return category
  if (m.unit === 'pages' && m.pagesLimit !== null) {
    return t('usage.remainingTooltip', {
      category,
      remaining: Math.max(0, m.pagesLimit - (m.pagesUsed ?? 0)).toLocaleString(),
      unit: t('usage.unitPages'),
    })
  }
  return t('usage.remainingPercentTooltip', { category, percent: `${Math.round(usageMeterRemaining.value)}%` })
})
const webChat = useWebChatStore()

const isSuperAdmin = computed(() => !!profile.value?.is_superadmin)

const navItems = computed(() => {
  // Gated features are simply absent from the rail when the org's plan lacks them (MAR-199) — no
  // disabled state, no upsell. Missing `profile` defaults to SHOWN only while the fetch is still
  // genuinely in flight (`status === 'idle'`), so the rail doesn't flash entries in and out on the
  // normal fast path. Once the fetch has resolved and failed (`'unavailable'` —
  // auth down, no session, no org) it defaults to HIDDEN: an unauthenticated or brokered session must
  // not read gated capability off a `?? true` fallback, even cosmetically — the server-side
  // `requireFeature` check still blocks the route/API either way, but the rail should not lie about it.
  const flags = profile.value?.feature_flags
  const fallback = profileStatus.value === 'idle'
  return [
    // Expedientes is ONE section over two sibling routes (MAR-130), so it needs `match`: it links to
    // the CLIENTS list (clients lead, #81 — it used to be cases) but must stay lit on /app/cases too,
    // which `startsWith(to)` alone cannot do now that neither path contains the other.
    { icon: Building2, to: '/app/clients', match: ['/app/clients', '/app/cases'], label: t('nav.clients'), show: flags?.case_management ?? fallback },
    { icon: TableProperties, to: '/app/sheets', label: t('sheets.title'), show: flags?.sheets ?? fallback },
    { icon: Landmark, to: '/app/laws', label: t('nav.laws') },
    // No contract review entry (MAR-1097): a review lives on its document, in the Revisión pane.
    { icon: FilePen, to: '/app/workflows', label: t('nav.workflows'), show: flags?.workflows ?? fallback },
  ].filter(item => item.show ?? true)
})

const favoriteChats = computed(() => webChat.conversations.filter(c => c.is_favorite))
// "Shared with me" (MAR-1417): pending shares, loaded once when the rail mounts.
const sharedChats = useSharedChatsStore()
const sharedWithMe = computed(() => sharedChats.shares)
onMounted(() => { void sharedChats.load() })
const recentChats = computed(() => webChat.conversations.filter(c => !c.is_favorite))

// The open chat, for highlighting its row. The store is the only source: a case-scoped chat stays in
// the docked panel and never navigates to /app/c/<id> (see ChatPanel), so the route would miss it, and
// on other id-bearing routes `params.id` is a different entity entirely. `loadConversation` sets this
// synchronously before its fetch, so there is no window where a chat is open and this is null.
const activeChatId = computed(() => webChat.conversationId)

/** `match` lets one entry own several routes (Expedientes = /app/cases + /app/clients); default is `to`. */
function isActive(item: { to: string; match?: string[] }) {
  return (item.match ?? [item.to]).some(prefix => route.path.startsWith(prefix))
}

function handleNavClick() {
  if (docsDrawerOpen.value && window.innerWidth < 1440) {
    docsDrawerOpen.value = false
  }
}

const chats = useChatsStore()
const router = useRouter()

// "New chat" is now a dropdown: start a chat, or upload a file straight into Documents.
const uploadTarget = useUploadTargetStore()
const fileInput = ref<HTMLInputElement | null>(null)

function startNewChat() {
  webChat.startNewChat()
  router.push('/app')
}

function openFilePicker() {
  fileInput.value?.click()
}

// Ask where the document belongs, then upload into the Documents root, revealing the drawer so the
// file is visible as it processes. The question and the upload both belong to `uploadTarget` (#83),
// which every entry point shares — this rail must not grow a second answer to it.
//
// KEPT when the global drop zone went (#83, 2026-09-05), and deliberately. `{path: '/'}` names a
// FOLDER, not a target: `requestUpload` is ask-then-upload, so `UploadTargetDialog` resolves the
// client, matter or conversation BEFORE any byte moves, and `confirm()` returns early without one.
// Nothing uploaded from here can land unattached, so this is not the catch-all that was removed.
function handleUpload(e: Event) {
  const input = e.target as HTMLInputElement
  const files = input.files
  const list = files ? Array.from(files) : []
  input.value = ''
  if (!list.length) return
  if (!docsDrawerOpen.value) toggleDocsDrawer()
  uploadTarget.requestUpload(list, { path: '/' })
}

// ── Right-edge 6px resize handle (MAR-1337) ──────────────────────────────────────────────────────
function stopResizeDrag() {
  setSidebarResizing(false)
  if (typeof window !== 'undefined') {
    window.removeEventListener('pointermove', onResizePointerMove)
    window.removeEventListener('mousemove', onResizePointerMove)
    window.removeEventListener('pointerup', onResizePointerUp)
    window.removeEventListener('mouseup', onResizePointerUp)
    window.removeEventListener('pointercancel', onResizePointerUp)
  }
}

function onResizePointerMove(e: PointerEvent | MouseEvent) {
  const outcome = handleSidebarDrag(e.clientX)
  if (outcome === 'collapsed') {
    stopResizeDrag()
  }
}

function onResizePointerUp() {
  stopResizeDrag()
}

function onResizePointerDown(e: PointerEvent | MouseEvent) {
  if (isSidebarCollapsed.value) return
  e.preventDefault()
  setSidebarResizing(true)
  window.addEventListener('pointermove', onResizePointerMove)
  window.addEventListener('mousemove', onResizePointerMove)
  window.addEventListener('pointerup', onResizePointerUp)
  window.addEventListener('mouseup', onResizePointerUp)
  window.addEventListener('pointercancel', onResizePointerUp)
}

onUnmounted(() => {
  stopResizeDrag()
  cancelDismissSidebarPeek()
})

// ── Onboarding reminders (MAR-933) ───────────────────────────────────────────────────────────────
// One bubble at a time, for the first step the user skipped (or that a plan upgrade unlocked),
// anchored to where that thing lives: documents → the Upload button, clients and cases → Expedientes.
// Derived with `computed` from template refs (no `watch()` per `apps/web/AGENTS.md`).
const { reminders, dismissReminder } = useOnboarding()
const railRoot = ref<HTMLElement | null>(null)
const uploadButton = ref<HTMLElement | null>(null)
const clientsNavButton = ref<HTMLElement | null>(null)
const reminder = computed<OptionalStep | null>(() => reminders.value[0] ?? null)

function setNavItemRef(to: string, el: unknown) {
  if (to !== '/app/clients') return
  if (el instanceof HTMLElement) {
    clientsNavButton.value = el
  } else if (el && typeof el === 'object' && '$el' in el && (el as { $el: unknown }).$el instanceof HTMLElement) {
    clientsNavButton.value = (el as { $el: HTMLElement }).$el
  } else {
    clientsNavButton.value = null
  }
}

const reminderAnchor = computed<HTMLElement | null>(() => {
  const step = reminder.value
  if (!step) return null
  if (isSidebarCollapsed.value && !isSidebarPeeking.value) return null
  if (step === 'documents') return uploadButton.value
  return clientsNavButton.value ?? railRoot.value?.querySelector<HTMLElement>('[data-nav="/app/clients"]') ?? null
})

async function actOnReminder() {
  const step = reminder.value
  if (!step) return
  // Retire it first — the write runs while the page moves, and a failed one only means the bubble
  // comes back on the next load, which is the honest outcome.
  void dismissReminder(step).catch(() => {})
  if (step === 'documents') openFilePicker()
  else await router.push(step === 'clients' ? '/app/clients/new' : '/app/cases/new')
}

function dismissCurrentReminder() {
  const step = reminder.value
  if (step) void dismissReminder(step).catch(() => {})
}

/** The ring on the icon the bubble points at: ink, never accent (MAR-1432 S1 — the active row is the rail's only amber). */
function ringed(key: 'documents' | 'expedientes'): boolean {
  const step = reminder.value
  if (!step || !reminderAnchor.value) return false
  return key === 'documents' ? step === 'documents' : step !== 'documents'
}

const renamingId = ref<string | null>(null)
const renameInput = ref('')
const renameInputEl = ref<HTMLInputElement | null>(null)

function startRename(conv: { id: string; title: string }) {
  renamingId.value = conv.id
  renameInput.value = conv.title
  nextTick(() => renameInputEl.value?.select())
}

async function submitRename(conv: { id: string; title: string }) {
  const title = renameInput.value.trim()
  if (title && title !== conv.title) {
    await chats.renameConversation(conv.id, title)
  }
  renamingId.value = null
}

function cancelRename() {
  renamingId.value = null
}

// The confirmation lives in `ChatItemMenu` itself now (MAR-474) — by the time this runs the user
// has already answered it in the menu.
async function deleteChat(conv: { id: string }) {
  await webChat.deleteConversation(conv.id)
  if (router.currentRoute.value.path === `/app/c/${conv.id}`) {
    router.replace('/app')
  }
}
</script>

<template>
  <aside
    ref="railRoot"
    data-sidebar-slot
    :data-sidebar-state="sidebarState"
    class="shrink-0 hidden sm:block h-full relative overflow-visible"
    :class="[
      isSidebarPeeking ? 'z-50' : 'z-20',
      isSidebarResizing ? 'transition-none' : 'transition-[width] duration-180 ease-[cubic-bezier(0.22,1,0.36,1)]',
    ]"
    :style="{ width: isSidebarCollapsed ? '0px' : `${sidebarWidth}px` }"
  >
    <div
      data-sidebar-panel
      class="flex flex-col pt-2 pb-1.5 px-(--app-rail-padding-horizontal) bg-sidebar text-sidebar-foreground select-none"
      :class="
        isSidebarCollapsed && isSidebarPeeking
          ? 'fixed top-2 bottom-10 left-2 z-50 rounded-[12px] border border-sidebar-border shadow-xl opacity-100 visible pointer-events-auto translate-x-0 transition-[opacity,transform,visibility] duration-170 ease-[cubic-bezier(0.22,1,0.36,1)]'
          : isSidebarCollapsed
            ? 'h-full invisible opacity-0 pointer-events-none -translate-x-3 overflow-hidden transition-[opacity,transform,visibility] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]'
            : 'h-full relative overflow-hidden opacity-100 visible translate-x-0'
      "
      :style="{ width: `${sidebarWidth}px` }"
      @mouseenter="cancelDismissSidebarPeek"
      @mouseleave="scheduleDismissSidebarPeek"
    >
      <!-- The brand row: mark, wordmark, jurisdiction, environment badge, Activity bell, and sidebar collapse/pin toggle (MAR-1337). -->
      <div class="flex w-full items-center mb-2">
        <NuxtLink
          to="/app"
          class="flex items-center gap-1 whitespace-nowrap leading-none rounded-md h-9 px-2 min-w-0"
          @click="webChat.startNewChat()"
        >
          <MarcusLogo class="h-5" />
          <span class="flex items-start gap-0.5">
            <span
              v-if="organizationMainJurisdiction"
              class="shrink-0 text-[8px] font-bold uppercase tracking-wide text-sidebar-muted leading-none"
              :title="jurisdictionName(organizationMainJurisdiction)"
            >{{ organizationMainJurisdiction }}</span>
          </span>
          <ClientOnly>
            <span
              v-if="appEnv !== 'prod'"
              class="shrink-0 self-center rounded border border-sidebar-border px-1 py-px text-[8px] font-bold uppercase tracking-wide leading-none text-sidebar-icon"
              :title="`Environment: ${appEnv}`"
            >{{ appEnv }}</span>
          </ClientOnly>
        </NuxtLink>

        <div class="ml-auto flex items-center gap-0.5 shrink-0">
          <ActivityButton :labelled="false" />
          <button
            type="button"
            data-sidebar-header-toggle
            class="flex h-7 w-7 items-center justify-center rounded-lg text-sidebar-icon hover:bg-sidebar-hover hover:text-sidebar-foreground transition-colors"
            :title="isSidebarCollapsed && isSidebarPeeking ? t('sidebar.expand') : t('sidebar.collapse')"
            :aria-label="isSidebarCollapsed && isSidebarPeeking ? t('sidebar.expand') : t('sidebar.collapse')"
            :aria-expanded="!isSidebarCollapsed"
            @click="toggleRail"
          >
            <component :is="isSidebarCollapsed && isSidebarPeeking ? PanelLeft : PanelLeftClose" class="h-4 w-4" />
          </button>
        </div>
      </div>

      <!-- New chat / Upload — two standalone buttons (#869). The new-item button is an OUTLINE
           (`border-input`, ink text, MAR-1432 S1) so the active nav row stays the rail's only solid fill. -->
      <button
        type="button"
        data-rail-new-item
        class="flex w-full items-center rounded-lg transition-colors mb-1 border border-input text-sidebar-foreground hover:bg-sidebar-hover h-9 gap-2.5 px-2.5"
        @click="startNewChat"
      >
        <MessageSquarePlus class="h-4 w-4 shrink-0 text-sidebar-icon" />
        <span class="text-[13px] truncate">{{ t('webChat.startNewChat') }}</span>
      </button>
      <button
        ref="uploadButton"
        type="button"
        class="flex w-full items-center rounded-lg transition-colors mb-2 border border-input text-sidebar-foreground hover:bg-sidebar-hover h-9 gap-2.5 px-2.5"
        :class="ringed('documents') ? 'ring-2 ring-sidebar-foreground' : ''"
        data-reminder-anchor="documents"
        @click="openFilePicker"
      >
        <Upload class="h-4 w-4 shrink-0 text-sidebar-icon" />
        <span class="text-[13px] truncate">{{ t('common.uploadFiles') }}</span>
      </button>
      <input ref="fileInput" type="file" class="hidden" :accept="ALLOWED_ACCEPT" multiple @change="handleUpload" />

      <!-- Nav items (solid `sidebar-active` fill with white ink per MAR-1316). -->
      <nav class="flex w-full flex-col items-center justify-center gap-0.5">
        <NuxtLink
          v-for="item in navItems"
          :key="item.label"
          :ref="(el) => setNavItemRef(item.to, el)"
          :to="item.to"
          :data-nav="item.to"
          class="relative flex items-center rounded-lg transition-colors h-9 w-full gap-2.5 px-2.5"
          :class="[
            item.to === '/app/clients' && ringed('expedientes') ? 'ring-2 ring-sidebar-foreground' : '',
            isActive(item)
              ? 'bg-sidebar-active text-sidebar-active-foreground font-medium'
              : 'text-sidebar-foreground hover:bg-sidebar-hover',
          ]"
          @click="handleNavClick()"
        >
          <component :is="item.icon" class="h-4 w-4 shrink-0" :class="isActive(item) ? 'text-sidebar-active-foreground' : 'text-sidebar-icon'" />
          <span class="text-[13px] truncate">{{ item.label }}</span>
        </NuxtLink>
      </nav>

      <!-- Chats lists -->
      <div
        v-if="(sharedWithMe.length > 0 || favoriteChats.length > 0 || recentChats.length > 0) && !route.path.startsWith('/app/history') && !route.path.startsWith('/app/runs')"
        class="mt-3 pt-3 border-t border-sidebar-border flex-1 min-h-0 overflow-hidden flex flex-col gap-3"
      >
        <!-- Shared with me (MAR-1417): pending shares; a continued one becomes an ordinary chat below. -->
        <div v-if="sharedWithMe.length > 0" data-testid="rail-shared-with-me">
          <p class="px-2.5 mb-1 text-[10px] font-medium text-sidebar-muted uppercase tracking-wider">{{ t('chatShare.sharedWithMe') }}</p>
          <NuxtLink
            v-for="share in sharedWithMe"
            :key="share.id"
            :to="`/app/shared/${share.id}`"
            class="flex items-center gap-2 w-full h-8 px-2.5 rounded-lg transition-colors"
            :class="route.path === `/app/shared/${share.id}`
              ? 'bg-sidebar-hover text-sidebar-foreground font-medium'
              : 'text-sidebar-foreground/90 hover:bg-sidebar-hover'"
            :title="share.shared_by_name ?? undefined"
          >
            <Share2 class="h-3 w-3 shrink-0" />
            <span class="text-[12px] truncate">{{ share.title || t('common.untitled') }}</span>
          </NuxtLink>
        </div>

        <!-- Favorites section -->
        <div v-if="favoriteChats.length > 0">
          <p class="px-2.5 mb-1 text-[10px] font-medium text-sidebar-muted uppercase tracking-wider">{{ t('history.favorites') }}</p>
          <div
            v-for="conv in favoriteChats"
            :key="conv.id"
            class="group flex items-center w-full h-8 px-2.5 rounded-lg transition-colors"
            :class="conv.id === activeChatId
              ? 'bg-sidebar-hover text-sidebar-foreground font-medium'
              : 'text-sidebar-foreground/90 hover:bg-sidebar-hover'"
          >
            <template v-if="renamingId === conv.id">
              <form class="flex-1 min-w-0" @submit.prevent="submitRename(conv)">
                <input
                  ref="renameInputEl"
                  v-model="renameInput"
                  class="w-full text-[11px] h-6 px-1 rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                  @keydown.escape="cancelRename"
                  @blur="submitRename(conv)"
                />
              </form>
            </template>
            <template v-else>
              <NuxtLink
                :to="`/app/c/${conv.id}`"
                class="flex items-center gap-2 flex-1 min-w-0 h-full"
                @click="webChat.loadConversation(conv.id)"
              >
                <Star class="h-3 w-3 shrink-0 fill-warning-ink text-warning-ink" />
                <span class="text-[12px] truncate">{{ conv.title || t('common.untitled') }}</span>
              </NuxtLink>
              <ChatItemMenu :conv="conv" @rename="startRename(conv)" @delete="deleteChat(conv)" />
            </template>
          </div>
        </div>

        <!-- Recent section -->
        <div v-if="recentChats.length > 0" class="flex-1 min-h-0 flex flex-col">
          <div class="flex items-center justify-between px-2.5 mb-1">
            <p class="text-[10px] font-medium text-sidebar-muted uppercase tracking-wider">{{ t('welcome.recent') }}</p>
            <NuxtLink
              to="/app/history"
              :title="t('nav.history')"
              class="flex items-center justify-center h-5 w-5 -mr-1 rounded-md text-sidebar-icon hover:text-sidebar-foreground hover:bg-sidebar-hover transition-colors"
            >
              <Clock class="h-3.5 w-3.5" />
            </NuxtLink>
          </div>
          <div class="flex-1 min-h-0 overflow-y-auto rail-scroll">
            <div
              v-for="conv in recentChats"
              :key="conv.id"
              class="group flex items-center w-full h-8 px-2.5 rounded-lg transition-colors"
              :class="conv.id === activeChatId
                ? 'bg-sidebar-hover text-sidebar-foreground font-medium'
                : 'text-sidebar-foreground/90 hover:bg-sidebar-hover'"
            >
              <template v-if="renamingId === conv.id">
                <form class="flex-1 min-w-0" @submit.prevent="submitRename(conv)">
                  <input
                    ref="renameInputEl"
                    v-model="renameInput"
                    class="w-full text-[11px] h-6 px-1 rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                    @keydown.escape="cancelRename"
                    @blur="submitRename(conv)"
                  />
                </form>
              </template>
              <template v-else>
                <NuxtLink
                  :to="`/app/c/${conv.id}`"
                  class="flex items-center gap-2 flex-1 min-w-0 h-full"
                  @click="webChat.loadConversation(conv.id)"
                >
                  <MessageSquare class="h-3 w-3 shrink-0" />
                  <span class="text-[12px] truncate">{{ conv.title || t('common.untitled') }}</span>
                </NuxtLink>
                <ChatItemMenu :conv="conv" @rename="startRename(conv)" @delete="deleteChat(conv)" />
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Spacer (only when no chats shown) -->
      <div
        v-if="(sharedWithMe.length === 0 && recentChats.length === 0 && favoriteChats.length === 0) || route.path.startsWith('/app/history') || route.path.startsWith('/app/runs')"
        class="flex-1"
      />

      <div class="w-full mt-2 pt-2 border-t border-sidebar-border flex flex-col">
        <!-- Documents toggle (bottom group) -->
        <button
          type="button"
          class="flex items-center rounded-lg transition-colors mb-1 h-9 w-full gap-2.5 px-2.5"
          :class="docsDrawerOpen
            ? 'bg-sidebar-hover text-sidebar-foreground'
            : 'text-sidebar-foreground hover:bg-sidebar-hover'"
          @click="toggleDocsDrawer"
        >
          <FolderOpen class="h-4 w-4 shrink-0" />
          <span class="text-[13px] truncate">{{ t('nav.documents') }}</span>
        </button>

        <!-- Chat toggle (bottom group) — hidden on the chat-home routes -->
        <button
          v-if="isContentRoute(route.path)"
          type="button"
          class="flex items-center rounded-lg transition-colors mb-1 h-9 w-full gap-2.5 px-2.5"
          :class="chatPanelOpen
            ? 'bg-sidebar-hover text-sidebar-foreground'
            : 'text-sidebar-foreground hover:bg-sidebar-hover'"
          @click="toggleChatPanel"
        >
          <MessageSquare class="h-4 w-4 shrink-0" />
          <span class="text-[13px] truncate">{{ t('nav.chat') }}</span>
        </button>

        <!-- Admin dashboard (superadmin only) -->
        <NuxtLink
          v-if="isSuperAdmin"
          to="/app/admin"
          class="flex items-center rounded-lg transition-colors mb-2 h-9 w-full gap-2.5 px-2.5"
          :class="route.path.startsWith('/app/admin')
            ? 'bg-sidebar-hover text-sidebar-foreground font-medium'
            : 'text-sidebar-foreground hover:bg-sidebar-hover'"
        >
          <Shield class="h-4 w-4 shrink-0" />
          <span class="text-[13px] truncate">{{ t('nav.admin') }}</span>
        </NuxtLink>
      </div>

      <!-- User section -->
      <NuxtLink
        to="/app/settings"
        class="flex items-center rounded-lg hover:bg-sidebar-hover transition-colors gap-2.5 px-2.5 py-2"
      >
        <div
          class="h-8 w-8 rounded-full bg-sidebar-hover text-sidebar-foreground border border-sidebar-border flex items-center justify-center text-[10px] font-semibold shrink-0 -ml-2"
        >
          {{ initials }}
        </div>
        <div class="flex-1 min-w-0">
          <p v-if="!route.path.startsWith('/app/settings/organization')" class="text-[13px] font-medium truncate text-sidebar-foreground">{{ displayName }}</p>
          <p v-if="organizationName && !profile?.org_override_id && !route.path.startsWith('/app/settings/organization')" class="text-[11px] text-sidebar-muted truncate">{{ organizationName }}</p>
          <!-- Neutral grey fill (`muted-foreground` on the `sidebar-hover` track, >= 3:1 both themes), not
               brown `bg-primary`: the active nav row stays the rail's only colour (MAR-1432 S1). -->
          <div v-if="usageMeter" class="mt-1.5" :title="usageMeterTitle">
            <div class="h-1 w-full overflow-hidden rounded-full bg-sidebar-hover">
              <div
                class="h-full rounded-full transition-all"
                :class="usageMeter.blocked ? 'bg-destructive' : usageMeter.percentUsed >= 80 ? 'bg-destructive/80' : 'bg-muted-foreground'"
                :style="{ width: `${usageMeterRemaining}%` }"
              />
            </div>
          </div>
        </div>
      </NuxtLink>

      <ReminderBubble
        v-if="reminder"
        :anchor="reminderAnchor"
        :title="t(`onboarding.reminders.${reminder}.title`)"
        :body="t(`onboarding.reminders.${reminder}.body`)"
        :action-label="t(`onboarding.reminders.${reminder}.action`)"
        @act="actOnReminder"
        @dismiss="dismissCurrentReminder"
      />
    </div>

    <!-- 6px Right-Edge Resize Handle (200px–360px real-time resize, <120px drag-to-collapse) -->
    <div
      v-if="!isSidebarCollapsed"
      data-sidebar-resize-handle
      :data-min-width="SIDEBAR_MIN_WIDTH"
      :data-max-width="SIDEBAR_MAX_WIDTH"
      class="group absolute top-2 bottom-0 -right-[3px] w-[6px] cursor-col-resize z-40 flex items-center justify-center select-none touch-none"
      @pointerdown="onResizePointerDown"
      @mousedown="onResizePointerDown"
    >
      <div
        class="w-0.5 h-full rounded-full transition-colors"
        :class="isSidebarResizing ? 'bg-accent' : 'bg-transparent group-hover:bg-accent/60'"
      />
    </div>
  </aside>
</template>

<style scoped>
/* Subtle scrollbar tuned for the frame ground — it follows `--color-sidebar-border`, so it tracks
   whichever theme is active (the OS default is wrong on both). */
.rail-scroll {
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--color-sidebar-muted) 30%, transparent) transparent;
}
.rail-scroll::-webkit-scrollbar {
  width: 6px;
}
.rail-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.rail-scroll::-webkit-scrollbar-thumb {
  background-color: color-mix(in srgb, var(--color-sidebar-muted) 30%, transparent);
  border-radius: 9999px;
}
.rail-scroll::-webkit-scrollbar-thumb:hover {
  background-color: color-mix(in srgb, var(--color-sidebar-muted) 50%, transparent);
}
</style>
